import { addDays } from "../dateNav";
import { todayKey } from "../format";
import type { UsageSnapshotInput } from "./plugins";

/**
 * Past-day usage sync ("history sync"). Today's snapshot freezes at whatever
 * the last same-day sync captured, so completed days must be re-fetched from
 * native once to store the final full-day total (matching iOS Settings). This
 * also backfills days on which the app was never opened.
 */

const SYNCED_THROUGH_KEY = "usageHistorySyncedThrough";
/**
 * First run looks this many days back — bounded by what the OS still retains
 * (iOS DeviceActivityData ~30 days; Android UsageStatsManager ~7 days, older
 * days come back empty-final and are skipped).
 */
export const HISTORY_BACKFILL_DAYS = 30;
const MAX_DAYS_PER_RUN = HISTORY_BACKFILL_DAYS;

export function readHistorySyncedThrough(): string | null {
  try {
    return localStorage.getItem(SYNCED_THROUGH_KEY);
  } catch {
    return null;
  }
}

function writeHistorySyncedThrough(date: string): void {
  try {
    localStorage.setItem(SYNCED_THROUGH_KEY, date);
  } catch {
    // Best-effort persistence; worst case the days are re-fetched next launch.
  }
}

/**
 * Completed days still needing a final sync, oldest first. `syncedThrough` is
 * the last finalized date (null on first run → full backfill window).
 */
export function datesNeedingHistorySync(today: string, syncedThrough: string | null): string[] {
  const yesterday = addDays(today, -1);
  const windowStart = addDays(today, -MAX_DAYS_PER_RUN);
  const start = syncedThrough === null || syncedThrough < windowStart
    ? windowStart
    : addDays(syncedThrough, 1);
  const dates: string[] = [];
  for (let date = start; date <= yesterday; date = addDays(date, 1)) {
    dates.push(date);
  }
  return dates;
}

export function snapshotHasUsage(snapshot: UsageSnapshotInput): boolean {
  return snapshot.totalSeconds > 0 || snapshot.topApps.some((app) => app.durationSeconds > 0);
}

/**
 * An empty granted past-day capture is final when native reported "no data"
 * (day out of retention or genuinely unused) — transient fetch failures must
 * be retried on the next run instead of being marked done.
 */
export function isFinalPastDaySnapshot(snapshot: UsageSnapshotInput): boolean {
  if (snapshot.permission !== "granted") return false;
  if (snapshotHasUsage(snapshot)) return true;
  return snapshot.reason === "no_usage_recorded_today" || snapshot.reason === undefined;
}

let historySyncInFlight = false;

/**
 * Fetches and syncs completed days sequentially, advancing the synced-through
 * marker only past days that produced a final capture. Stops at the first
 * non-final day so transient native failures retry on the next app open.
 * `collectDay` is injected (see collectUsageForDay) to keep this module free
 * of Capacitor imports.
 */
export async function runUsageHistorySync(
  collectDay: (date: string) => Promise<UsageSnapshotInput | null>,
  syncSnapshot: (snapshot: UsageSnapshotInput) => Promise<unknown>,
): Promise<void> {
  if (historySyncInFlight) return;
  const today = todayKey();
  const dates = datesNeedingHistorySync(today, readHistorySyncedThrough());
  if (dates.length === 0) return;

  historySyncInFlight = true;
  try {
    for (const date of dates) {
      const snapshot = await collectDay(date);
      if (snapshot === null || !isFinalPastDaySnapshot(snapshot)) return;
      if (snapshotHasUsage(snapshot)) {
        await syncSnapshot({ ...snapshot, date });
      }
      writeHistorySyncedThrough(date);
    }
  } finally {
    historySyncInFlight = false;
  }
}
